import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./page-banner.css";

const availableClipFiles = [
  [1, "png"], [2, "png"], [3, "webp"], [4, "webp"], [5, "webp"],
  [6, "png"], [7, "webp"], [8, "webp"], [9, "webp"], [10, "webp"],
  [11, "webp"], [12, "webp"], [13, "webp"], [14, "jpg"], [15, "webp"],
  [16, "webp"], [17, "webp"], [18, "webp"], [19, "webp"], [20, "webp"],
  [23, "webp"], [24, "webp"], [25, "webp"],
  [26, "webp"], [27, "webp"], [29, "webp"], [30, "webp"],
  [32, "webp"], [33, "webp"], [34, "webp"],
  [35, "webp"], [36, "webp"], [37, "webp"], [38, "jpg"],
];

const allNewsClips = availableClipFiles.map(([id, extension]) => {
  const number = String(id).padStart(2, "0");

  return {
    id,
    image: `/images/news-clips/news-clip-${number}.${extension}`,
    titleKey: `newsClipsPage.clips.${id}.title`,
    descriptionKey: `newsClipsPage.clips.${id}.description`,
  };
});

const NewsClips = () => {
  const { t } = useTranslation();
  const [selectedClip, setSelectedClip] = useState(null);
  const getClipTitle = (clip) => t(clip.titleKey);
  const getClipDescription = (clip) => t(clip.descriptionKey);

  useEffect(() => {
    if (!selectedClip) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedClip(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedClip]);

  return (
    <>
      <Helmet>
        <title>Harisharan Devgan News Clips | Newspaper Coverage</title>
        <meta name="description" content="Browse newspaper clippings and media coverage featuring Harisharan Devgan, including news about his work, public engagements, views on agriculture, farmers and national affairs." />
        <link rel="canonical" href="https://harisharandevgan.com/news-clips/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Harisharan Devgan News Clips | Newspaper Coverage" />
        <meta property="og:description" content="Browse newspaper clippings and media coverage featuring Harisharan Devgan, including news about his work, public engagements, views on agriculture, farmers and national affairs." />
        <meta property="og:url" content="https://harisharandevgan.com/news-clips/" />
        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:image" content="https://harisharandevgan.com/images/news-banner.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harisharan Devgan News Clips | Newspaper Coverage" />
        <meta name="twitter:description" content="Browse newspaper clippings and media coverage featuring Harisharan Devgan, including news about his work, public engagements, views on agriculture, farmers and national affairs." />
        <meta name="twitter:image" content="https://harisharandevgan.com/images/news-banner.webp" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
      </Helmet>

      <section className="relative min-h-[400px] flex items-center mt-[98px] overflow-hidden">
        <img
          src="/images/prkit-banner.webp"
          alt={t("newsClipsPage.bannerAlt")}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="600"
          className="page-banner-image page-banner-image--news-clips absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative max-w-7xl mx-auto w-full px-6 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 uppercase tracking-wider">{t("newsClipsPage.title")}</h1>
            <p className="text-lg text-gray-300">{t("newsClipsPage.subtitle")}</p>
          </div>
          <div className="md:text-right">
            <h2 className="text-lg font-semibold text-white">
              <Link to="/" className="text-gray-300 hover:text-orange-500 transition">{t("common.home")}</Link>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-orange-500">{t("newsClipsPage.title")}</span>
            </h2>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 px-4 font-sans">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gray-500 text-xs tracking-[0.3em] uppercase mb-4">{t("newsClipsPage.title")}</p>
            <h2 className="text-4xl md:text-5xl font-light tracking-[0.2em] text-black uppercase">{t("newsClipsPage.latestPress")}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {allNewsClips.map((article) => (
              <article key={article.id} className="flex flex-col group">
                <div className="aspect-[4/3] overflow-hidden mb-6 relative bg-gray-100">
                  <button type="button" onClick={() => setSelectedClip(article)} className="block w-full h-full cursor-zoom-in" aria-label={`${t("newsClipsPage.open")} ${getClipTitle(article)}`}>
                    <img src={article.image} alt={getClipTitle(article)} title={getClipTitle(article)} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </button>
                </div>
                <h3 className="text-black font-bold text-lg tracking-widest uppercase mb-3">{getClipTitle(article)}</h3>
                <p className="text-gray-700 text-sm leading-relaxed line-clamp-2">{getClipDescription(article)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {selectedClip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" role="dialog" aria-modal="true" aria-label={getClipTitle(selectedClip)} onClick={() => setSelectedClip(null)}>
          <button type="button" onClick={() => setSelectedClip(null)} className="absolute right-4 top-4 text-3xl leading-none text-white" aria-label={t("newsClipsPage.closeViewer")}>&times;</button>
          <img src={selectedClip.image} alt={getClipTitle(selectedClip)} className="max-h-[90vh] max-w-full object-contain" onClick={(event) => event.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default NewsClips;